/**
 * Besucherstrom für die Feature-Seite Website-Statistik.
 *
 * Ein einziger Strom fließt hinter dem Inhalt über die ganze Seite. Sein Weg ergibt sich aus
 * Ankerpunkten im Markup (data-stream-point), die Breite aus den Besucherzahlen. Unterwegs
 * münden Zuflüsse ein, der Strom läuft zu einem Punkt zusammen und taucht ab, an jeder Stufe zweigt
 * zum Rand hin ab, wer nicht weitergeht, und die Buchungen fließen am Ende hinaus.
 *
 * Der Strom besteht aus feinen Fasern, die zum Rand hin ausdünnen, auf denen einzelne Besucher
 * als kurze Lichtscheine entlangwandern. Die Fasern werden in Kacheln gezeichnet und
 * zwischengespeichert, ein fest stehendes Canvas zeigt davon nur den sichtbaren Ausschnitt.
 */

const DESKTOP_QUERY = '(min-width: 1024px)';
const MAX_DPR = 2;
const TILE = 1024;

const COLORS = {
    primary300: '#a5b4fc',
    primary400: '#818cf8',
    primary500: '#6366f1',
    primary600: '#4f46e5',
    primary700: '#4338ca',
    cyan400: '#22d3ee',
    cyan500: '#06b6d4',
    cyan600: '#0891b2',
    surface400: '#9ca3af',
    surface500: '#6b7280',
};

const rgb = (hex) => {
    const n = parseInt(hex.slice(1), 16);

    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const mix = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const smooth = (t) => t * t * (3 - 2 * t);
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const css = (c, a) => `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${a})`;

// Deterministischer Zufall, damit die Fasern nach einer Größenänderung gleich liegen
function seeded(seed) {
    let s = seed >>> 0;

    return () => {
        s = (s + 0x6d2b79f5) >>> 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);

        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function gaussian(rand) {
    let u = 0;
    while (u === 0) u = rand();

    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * rand());
}

/* ------------------------------------------------------------------ Verlauf des Stroms */

/**
 * Baut aus den Ankerpunkten die einzelnen Wege. Jeder Weg steht für eine Gruppe von Besuchern
 * mit gleicher Herkunft und gleichem Ausgang (abgezweigt vor der Suche, ohne Treffer, ohne
 * Warenkorb, ohne Buchung oder gebucht).
 *
 * Der Strom ist nicht überall zu sehen. Zwischen seinen Auftritten ist er unsichtbar (a = 0)
 * und taucht an der nächsten Stelle wieder auf, wie ein Fluss, der unter der Erde weiterläuft.
 * Jeder Punkt gibt an, ob der Strom ihn senkrecht (v) oder waagerecht (h) durchfließt.
 */
function buildRoutes(data, p, width, desktop) {
    const perVisitor = desktop ? 0.045 : 0.025;
    const w = (n) => Math.max(1.5, n * perVisitor);
    const edge = width + 60;
    const pt = (point, n, a, c, t = 'v') => ({ x: point.x, y: point.y, w: w(n), a, c: rgb(c), t });

    const [sessions, searches, withResults, carts, bookings] = data.stages;
    const empty = searches - withResults;
    const row = p['stage-sessions'].y;
    const at = (name) => ({ x: p[name].x, y: row });

    const origins = [
        { n: sessions - data.referrers.reduce((sum, r) => sum + r.visits, 0), color: COLORS.primary300, fromTop: true },
        ...data.referrers.map((referrer, i) => ({
            n: referrer.visits,
            color: [COLORS.primary600, COLORS.cyan600, COLORS.primary400, COLORS.surface500, COLORS.surface400][i],
            y: p[`source-${i}`].y,
        })),
    ];
    const fates = [
        { key: 'search', n: sessions - searches },
        { key: 'miss', n: empty },
        { key: 'cart', n: withResults - carts },
        { key: 'book', n: carts - bookings },
        { key: 'end', n: bookings },
    ];

    // Wer an einer Stufe nicht weitergeht, sinkt schräg ab und verläuft sich
    const drop = (stage, n, side) => [
        pt({ x: stage.x + side * (desktop ? 90 : 30), y: row + (desktop ? 110 : 60) }, n * 0.6, 0.6, COLORS.primary300),
        pt({ x: stage.x + side * (desktop ? 170 : 55), y: row + (desktop ? 230 : 120) }, n * 0.8, 0, COLORS.primary300),
    ];

    const trunk = COLORS.primary500;
    const routes = [];

    origins.forEach((origin, o) => {
        const start = origin.fromTop
            ? [pt({ x: p.origin.x, y: p.origin.y - 40 }, origin.n, 0, origin.color), pt({ x: p.origin.x, y: p.origin.y + 120 }, origin.n, 1, origin.color)]
            : [pt({ x: edge, y: origin.y }, origin.n, 0, origin.color, 'h'), pt({ x: edge - (desktop ? 160 : 40), y: origin.y + 4 }, origin.n, 1, origin.color, 'h')];

        fates.forEach((fate, f) => {
            const count = (origin.n * fate.n) / sessions;
            if (count < 0.5) return;

            const points = [
                ...start,
                // Erster Auftritt: die Zuflüsse vereinen sich und laufen in den Kreis über der Herkunft
                pt(p.confluence, sessions, 1, origin.color),
                pt({ x: p.node.x, y: p.node.y - (desktop ? 90 : 70) }, sessions * 0.6, 1, origin.color),
                pt(p.node, 180, 1, trunk),
                // Im Kreis taucht der Strom ab
                pt({ x: p.node.x, y: p.node.y + 50 }, sessions * 0.3, 0, trunk),
                // Unsichtbar weiter bis zum linken Rand, dort taucht er waagerecht wieder auf
                pt({ x: -60, y: row }, sessions, 0, trunk, 'h'),
                pt({ x: Math.max(0, p['stage-sessions'].x - (desktop ? 180 : 40)), y: row }, sessions, 1, trunk, 'h'),
                pt(at('stage-sessions'), sessions, 1, trunk, 'h'),
                pt(at('stage-search'), sessions, 1, trunk, 'h'),
            ];

            if (fate.key === 'search') {
                points.push(...drop(at('stage-search'), fate.n, 1));
            } else if (fate.key === 'miss') {
                points.push(
                    pt({ x: p['stage-search'].x + (desktop ? 60 : 20), y: row + 30 }, empty * 1.4, 1, COLORS.primary600),
                    pt(p.gather, 20, 1, COLORS.primary700),
                );
            } else {
                points.push(pt(at('stage-cart'), withResults, 1, trunk, 'h'));

                if (fate.key === 'cart') {
                    points.push(...drop(at('stage-cart'), fate.n, 1));
                } else {
                    points.push(pt(at('stage-book'), carts, 1, COLORS.primary600, 'h'));

                    if (fate.key === 'book') {
                        points.push(...drop(at('stage-book'), fate.n, 1));
                    } else {
                        // Die Buchungen fließen nach rechts hinaus und verlaufen sich
                        points.push(
                            pt({ x: Math.min(width - 20, p['stage-book'].x + (desktop ? 150 : 30)), y: row }, bookings, 1, COLORS.cyan500, 'h'),
                            pt({ x: edge, y: row + 1 }, bookings, 0, COLORS.cyan500, 'h'),
                        );
                    }
                }
            }

            routes.push({
                points,
                strands: Math.max(1, Math.round((count / 5) * (fate.key === 'end' ? 2.5 : fate.key === 'miss' ? 2 : 1))),
                particles: count / (fate.key === 'end' ? 4 : fate.key === 'miss' ? 5 : 32),
                color: rgb(fate.key === 'end' ? COLORS.cyan600 : fate.key === 'miss' ? COLORS.primary700 : COLORS.primary600),
                gather: fate.key === 'miss',
                seed: o * 10 + f,
            });
        });
    });

    const glows = [
        { ...p.gather, r: desktop ? 60 : 36, c: rgb(COLORS.primary400), a: 0.22 },
    ];

    return { routes, glows };
}

// Tastet einen Weg etwa alle 4 px ab. Jeder Punkt wird senkrecht (v) oder waagerecht (h) durchflossen.
function sampleRoute(points) {
    const samples = [];

    for (let i = 1; i < points.length; i++) {
        const p0 = points[i - 1];
        const p1 = points[i];
        const dx = p1.x - p0.x;
        const dy = p1.y - p0.y;
        const c0 = p0.t === 'h' ? [p0.x + dx / 2, p0.y] : [p0.x, p0.y + dy / 2];
        const c1 = p1.t === 'h' ? [p1.x - dx / 2, p1.y] : [p1.x, p1.y - dy / 2];
        const steps = Math.max(4, Math.ceil(Math.hypot(dx, dy) / 4));

        for (let s = i === 1 ? 0 : 1; s <= steps; s++) {
            const t = s / steps;
            const mt = 1 - t;
            const e = smooth(t);
            const b0 = mt * mt * mt;
            const b1 = 3 * mt * mt * t;
            const b2 = 3 * mt * t * t;
            const b3 = t * t * t;

            samples.push({
                x: b0 * p0.x + b1 * c0[0] + b2 * c1[0] + b3 * p1.x,
                y: b0 * p0.y + b1 * c0[1] + b2 * c1[1] + b3 * p1.y,
                w: p0.w + (p1.w - p0.w) * e,
                a: p0.a + (p1.a - p0.a) * e,
                c: mix(p0.c, p1.c, e),
            });
        }
    }

    for (let i = 0; i < samples.length; i++) {
        const prev = samples[Math.max(0, i - 1)];
        const next = samples[Math.min(samples.length - 1, i + 1)];
        const len = Math.hypot(next.x - prev.x, next.y - prev.y) || 1;
        samples[i].nx = -(next.y - prev.y) / len;
        samples[i].ny = (next.x - prev.x) / len;
    }

    return samples;
}

function buildStrands(route, samples, rand) {
    const n = samples.length;

    return Array.from({ length: route.strands }, () => {
        // Normalverteilt: dichter Kern, weich auslaufender Rand
        const offset = clamp(gaussian(rand) * 0.42, -1.05, 1.05);
        const amplitude = 0.05 + rand() * 0.14;
        const waves = (n / 250) * (0.6 + rand() * 1.4);
        const phase = rand() * Math.PI * 2;
        const xy = new Float32Array(n * 2);

        for (let i = 0; i < n; i++) {
            const s = samples[i];
            const u = offset + amplitude * Math.sin((i / n) * waves * Math.PI * 2 + phase);
            xy[i * 2] = s.x + s.nx * s.w * u;
            xy[i * 2 + 1] = s.y + s.ny * s.w * u;
        }

        const edgeFade = 1 - Math.pow(Math.min(1, Math.abs(offset)), 3) * 0.7;

        return { xy, alpha: (0.5 + rand() * 0.9) * edgeFade };
    });
}

/* ------------------------------------------------------------------ Darstellung */

class VisitorStream {
    constructor(root) {
        this.root = root;
        this.canvas = root.querySelector('[data-stream-canvas]');
        this.ctx = this.canvas.getContext('2d');
        this.data = JSON.parse(root.dataset.visitorStream);
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.tiles = new Map();
        this.last = performance.now();
        this.frame = this.frame.bind(this);

        const schedule = () => {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => this.build(), 120);
        };
        new ResizeObserver(schedule).observe(root);
        window.addEventListener('resize', schedule);
        if (this.reducedMotion) window.addEventListener('scroll', () => this.render(0), { passive: true });

        this.build();
        requestAnimationFrame(this.frame);
    }

    anchors() {
        const base = this.root.getBoundingClientRect();
        const points = {};

        this.root.querySelectorAll('[data-stream-point]').forEach((element) => {
            const rect = element.getBoundingClientRect();
            points[element.dataset.streamPoint] = {
                x: rect.left + rect.width / 2 - base.left,
                y: rect.top + rect.height / 2 - base.top,
            };
        });

        return points;
    }

    build() {
        const base = this.root.getBoundingClientRect();
        this.width = base.width;
        this.dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
        this.canvas.width = Math.round(document.documentElement.clientWidth * this.dpr);
        this.canvas.height = Math.round(window.innerHeight * this.dpr);
        this.tiles.clear();

        const desktop = window.matchMedia(DESKTOP_QUERY).matches;
        const { routes, glows } = buildRoutes(this.data, this.anchors(), this.width, desktop);
        this.glows = glows;
        this.strandAlpha = desktop ? 0.14 : 0.12;

        this.routes = routes.map((route) => {
            const rand = seeded(route.seed + 1);
            const samples = sampleRoute(route.points);
            const strands = buildStrands(route, samples, rand);
            let length = 0;
            for (let i = 1; i < samples.length; i++) {
                length += Math.hypot(samples[i].x - samples[i - 1].x, samples[i].y - samples[i - 1].y);
            }
            // Bruchteile als Wahrscheinlichkeit, damit kleine Gruppen nicht ganz verschwinden
            const count = Math.floor(route.particles) + (rand() < route.particles % 1 ? 1 : 0);

            return {
                ...route,
                samples,
                strands,
                ys: Float32Array.from(samples, (s) => s.y),
                step: length / (samples.length - 1),
                particleList: Array.from({ length: count }, () => this.spawn(strands, samples.length, rand, true)),
            };
        });

        this.render(0);
    }

    spawn(strands, count, rand = Math.random, anywhere = false) {
        return {
            strand: Math.floor(rand() * strands.length),
            index: anywhere ? rand() * (count - 1) : 0,
            speed: 0.75 + rand() * 0.5,
            trail: 16 + rand() * 22,
            life: anywhere ? 1 : 0,
        };
    }

    // Erster Index, dessen y nicht kleiner als y ist (die Wege fließen stets nach unten)
    static search(ys, y) {
        let lo = 0;
        let hi = ys.length - 1;
        while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (ys[mid] < y) lo = mid + 1;
            else hi = mid;
        }

        return lo;
    }

    tile(index) {
        if (this.tiles.has(index)) return this.tiles.get(index);

        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(this.width * this.dpr);
        canvas.height = TILE * this.dpr;
        const ctx = canvas.getContext('2d');
        const top = index * TILE;
        const bottom = top + TILE;
        ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, -top * this.dpr);
        ctx.globalCompositeOperation = 'multiply';
        ctx.lineWidth = 0.75;
        ctx.lineCap = 'butt';

        for (const route of this.routes) {
            const { samples, ys } = route;
            const from = Math.max(0, VisitorStream.search(ys, top - 60) - 1);
            const to = Math.min(samples.length - 1, VisitorStream.search(ys, bottom + 60) + 1);
            if (to - from < 1) continue;

            let visible = false;
            for (let i = from; i <= to; i++) {
                if (samples[i].a > 0.01) {
                    visible = true;
                    break;
                }
            }
            if (!visible) continue;

            // Abschnittsweise, jeder Abschnitt mit einem Verlauf in seiner eigenen Laufrichtung
            const chunk = 24;
            for (const strand of route.strands) {
                const { xy } = strand;
                const alpha = this.strandAlpha * strand.alpha;

                for (let i = from; i < to; i += chunk) {
                    const j = Math.min(to, i + chunk);
                    const m = (i + j) >> 1;
                    if (samples[i].a < 0.01 && samples[m].a < 0.01 && samples[j].a < 0.01) continue;

                    // Farbstufe alle drei Punkte, damit Ein- und Ausblenden genau an ihrer Stelle bleiben
                    const gradient = ctx.createLinearGradient(xy[i * 2], xy[i * 2 + 1], xy[j * 2], xy[j * 2 + 1]);
                    for (let k = i; k <= j; k += 3) {
                        gradient.addColorStop((k - i) / (j - i), css(samples[k].c, alpha * samples[k].a));
                    }
                    gradient.addColorStop(1, css(samples[j].c, alpha * samples[j].a));
                    ctx.strokeStyle = gradient;
                    ctx.beginPath();
                    ctx.moveTo(xy[i * 2], xy[i * 2 + 1]);
                    for (let k = i + 1; k <= j; k++) ctx.lineTo(xy[k * 2], xy[k * 2 + 1]);
                    ctx.stroke();
                }
            }
        }

        ctx.globalCompositeOperation = 'source-over';
        for (const glow of this.glows) {
            if (glow.y + glow.r < top || glow.y - glow.r > bottom) continue;
            const gradient = ctx.createRadialGradient(glow.x, glow.y, 0, glow.x, glow.y, glow.r);
            gradient.addColorStop(0, css(glow.c, glow.a));
            gradient.addColorStop(0.4, css(glow.c, glow.a * 0.3));
            gradient.addColorStop(1, css(glow.c, 0));
            ctx.fillStyle = gradient;
            ctx.fillRect(glow.x - glow.r, glow.y - glow.r, glow.r * 2, glow.r * 2);
        }

        // Nur die Kacheln rund um den sichtbaren Bereich behalten
        if (this.tiles.size > 6) {
            const far = [...this.tiles.keys()].sort((a, b) => Math.abs(b - index) - Math.abs(a - index))[0];
            this.tiles.delete(far);
        }
        this.tiles.set(index, canvas);

        return canvas;
    }

    frame(now) {
        const dt = Math.min(0.05, (now - this.last) / 1000);
        this.last = now;
        if (!document.hidden) this.render(this.reducedMotion ? 0 : dt);
        if (!this.reducedMotion) requestAnimationFrame(this.frame);
    }

    render(dt) {
        if (!this.routes) return;

        const ctx = this.ctx;
        const base = this.root.getBoundingClientRect();
        const viewport = window.innerHeight;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        if (base.bottom < 0 || base.top > viewport) return;

        ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        const viewTop = -base.top;
        for (let i = Math.max(0, Math.floor(viewTop / TILE)); i <= Math.floor((viewTop + viewport) / TILE); i++) {
            ctx.drawImage(this.tile(i), base.left, base.top + i * TILE, this.width, TILE);
        }

        ctx.lineCap = 'round';
        for (const route of this.routes) {
            const { samples, strands } = route;
            const last = samples.length - 1;

            for (const particle of route.particleList) {
                // Vor dem Ziel werden die Punkte langsam und sammeln sich
                const progress = particle.index / last;
                const profile = route.gather ? 0.05 + 0.95 * Math.pow(clamp((1 - progress) / 0.25, 0, 1), 1.4) : 1;
                const underground = samples[Math.min(last, Math.floor(particle.index))].a < 0.05 ? 6 : 1;
                particle.index += (70 * particle.speed * profile * underground * dt) / route.step;
                particle.life = Math.min(1, particle.life + dt * 1.5);
                if (!(particle.index < last)) Object.assign(particle, this.spawn(strands, samples.length));

                const i = Math.min(last - 1, Math.floor(particle.index));
                const f = particle.index - i;
                const xy = strands[particle.strand].xy;
                const x = base.left + xy[i * 2] + (xy[i * 2 + 2] - xy[i * 2]) * f;
                const y = base.top + xy[i * 2 + 1] + (xy[i * 2 + 3] - xy[i * 2 + 1]) * f;
                if (y < -60 || y > viewport + 60) continue;

                let alpha = samples[i].a * particle.life;
                if (route.gather) alpha *= 0.35 + 0.65 * clamp((last - particle.index) / (last * 0.02), 0, 1);
                if (alpha < 0.03) continue;

                // Ein Lichtschein, der entlang der Faser wandert: nach vorn und hinten gleich weich, ohne Kopf
                const reach = Math.round((particle.trail * Math.max(profile, 0.25)) / route.step);
                const back = Math.max(0, i - reach);
                const ahead = Math.min(last, i + reach);
                if (ahead - back < 2) continue;

                const glint = ctx.createLinearGradient(base.left + xy[back * 2], base.top + xy[back * 2 + 1], base.left + xy[ahead * 2], base.top + xy[ahead * 2 + 1]);
                glint.addColorStop(0, css(route.color, 0));
                glint.addColorStop(0.5, css(route.color, alpha * 0.8));
                glint.addColorStop(1, css(route.color, 0));
                ctx.strokeStyle = glint;
                ctx.lineWidth = 1.2;
                ctx.beginPath();
                ctx.moveTo(base.left + xy[back * 2], base.top + xy[back * 2 + 1]);
                for (let k = back + 1; k <= ahead; k++) ctx.lineTo(base.left + xy[k * 2], base.top + xy[k * 2 + 1]);
                ctx.stroke();
            }
        }
    }
}

document.querySelectorAll('[data-visitor-stream]').forEach((root) => new VisitorStream(root));
